import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type SetStateAction,
} from "react";
import { Dialog, Popover } from "react-aria-components";

import { useAppSelector } from "@/app/hooks";
import { selectAuthActor } from "@/features/auth/authSessionSlice";
import {
  buildMerryChatAddMenuCatalog,
  emptyMerryChatAddMenuCatalog,
} from "@/features/chat/chatAddMenuCatalog";
import {
  buildMerryChatExtensionViewModels,
  buildMerchantChatCapabilityIntent,
  projectMerryChatExtensionViewModels,
  createCapabilityExtensionSelection,
  createToolExtensionSelection,
  imageExtensionParameters,
  removeMerryChatExtension,
  serializeMerryChatExtensions,
  updateMerryChatExtensionParameters,
  upsertMerryChatExtension,
  videoExtensionParameters,
} from "@/features/chat/chatExtensions";
import { ChatAttachmentTray } from "@/features/chat/ChatAttachmentTray";
import {
  hasUnreadyChatAttachments,
  mergeChatAttachments,
  readyAttachmentFromResource,
  readyChatResourceIds,
  readyChatResourceRefs,
  type ChatAttachmentResourceRef,
  type ChatAttachment,
} from "@/features/chat/chatAttachments";
import {
  flattenMyFilesResourcePages,
  useGetMyFilesResourceAccessUrlMutation,
  useGetMyFilesResourcesInfiniteQuery,
  type MyFilesResourceTypeFilter,
} from "@/features/my-files/myFilesApi";
import { ChatAddInput, type ChatAddInputProps } from "@/shared/ui/ChatAddInput";
import { ChatInputError } from "@/shared/ui/chatInputError";
import {
  MerryChatAddMenu,
  type MerryChatAddMenuAssetState,
  type MerryChatAddMenuCategory,
} from "@/shared/ui/MerryChatAddMenu";
import type {
  MerryChatExtensionPayload,
  MerryChatExtensionSelection,
} from "@/shared/ui/chatExtensionTypes";
import type { MerchantChatCapabilityIntent } from "@/features/chat/chatExtensions";
import {
  defaultMerryImageParameters,
  defaultMerryVideoParameters,
  type MerryChatAddMenuView,
  type MerryImageParameters,
  type MerryVideoParameters,
} from "@/shared/ui/merryChatAddMenuVariants";

const MAX_CATEGORY_COUNT_PAGES = 5;
const EMPTY_PREVIEW_URLS: Readonly<Record<string, string>> = {};
const EMPTY_ATTACHMENTS: ReadonlyArray<ChatAttachment> = [];
const EMPTY_EXTENSIONS: ReadonlyArray<MerryChatExtensionSelection> = [];

export interface ChatAddMenuSendContext {
  capabilityIntent?: MerchantChatCapabilityIntent;
  extensions?: ReadonlyArray<MerryChatExtensionPayload>;
  resourceIds: ReadonlyArray<string>;
  resourceRefs: ReadonlyArray<ChatAttachmentResourceRef>;
}

export interface ChatAddMenuComposerProps
  extends Omit<
    ChatAddInputProps,
    "isAddMenuOpen" | "isSendDisabled" | "onAdd" | "onSend"
  > {
  attachments?: ReadonlyArray<ChatAttachment>;
  defaultExtensions?: ReadonlyArray<MerryChatExtensionPayload>;
  onAuthenticationRequired?: (text: string, context: ChatAddMenuSendContext) => void;
  className?: string;
  presentation?: "default" | "figma";
  contentMode?: "all" | "resources-only";
  isAISendDisabled?: boolean;
  isUploadDisabled?: boolean;
  onAttachmentsChange?: (attachments: ReadonlyArray<ChatAttachment>) => void;
  onResourceRefsChange?: (resourceIds: ReadonlyArray<string>) => void;
  onRetryAttachment?: (attachmentId: string) => void;
  onUploadFiles?: (files: ReadonlyArray<File>) => string | null | void;
  onSend?: (
    message: string,
    context: ChatAddMenuSendContext,
  ) => Promise<void> | void;
}

function categoryResourceType(
  category: MerryChatAddMenuCategory,
): MyFilesResourceTypeFilter {
  return category.kind;
}

export function ChatAddMenuComposer({
  attachments: controlledAttachments,
  defaultExtensions,
  onAuthenticationRequired,
  className,
  contentMode = "all",
  presentation = "default",
  isAISendDisabled = false,
  isUploadDisabled = false,
  onAttachmentsChange,
  onResourceRefsChange,
  onRetryAttachment,
  onUploadFiles,
  onSend,
  ...inputProps
}: ChatAddMenuComposerProps) {
  const actor = useAppSelector(selectAuthActor);
  const organizationId = actor?.activeOrganizationId ?? "";
  const triggerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const previewRequestsRef = useRef(new Map<string, Promise<string>>());
  const [isOpen, setIsOpen] = useState(false);
  const [uploadNotice, setUploadNotice] = useState("");
  const [view, setView] = useState<MerryChatAddMenuView>("default");
  const [overflowExtensionIds, setOverflowExtensionIds] = useState<
    ReadonlyArray<string>
  >([]);
  const [activeCategoryId, setActiveCategoryId] = useState("all");
  const [resourceType, setResourceType] =
    useState<MyFilesResourceTypeFilter>("all");
  const [previewState, setPreviewState] = useState<{
    organizationId: string;
    urls: Record<string, string>;
  }>({ organizationId, urls: {} });
  const [attachmentState, setAttachmentState] = useState<{
    organizationId: string;
    attachments: ReadonlyArray<ChatAttachment>;
  }>({ organizationId, attachments: [] });
  const [videoParameters, setVideoParameters] = useState<MerryVideoParameters>(
    defaultMerryVideoParameters,
  );
  const [imageParameters, setImageParameters] = useState<MerryImageParameters>(
    defaultMerryImageParameters,
  );
  const [activeExtensionState, setActiveExtensionState] = useState<{
    extensionId?: string;
    organizationId: string;
  }>({ organizationId });
  const [selectedExtensionState, setSelectedExtensionState] = useState<{
    organizationId: string;
    selections: ReadonlyArray<MerryChatExtensionSelection>;
  }>({ organizationId, selections: projectMerryChatExtensionViewModels(defaultExtensions ?? []) });
  const activeExtensionId =
    activeExtensionState.organizationId === organizationId
      ? activeExtensionState.extensionId
      : undefined;
  const selectedExtensions =
    selectedExtensionState.organizationId === organizationId
      ? selectedExtensionState.selections
      : EMPTY_EXTENSIONS;
  const setActiveExtensionId = useCallback(
    (extensionId?: string) => {
      setActiveExtensionState({ extensionId, organizationId });
    },
    [organizationId],
  );
  const setSelectedExtensions = useCallback(
    (update: SetStateAction<ReadonlyArray<MerryChatExtensionSelection>>) => {
      setSelectedExtensionState((current) => {
        const currentSelections =
          current.organizationId === organizationId ? current.selections : [];
        return {
          organizationId,
          selections:
            typeof update === "function"
              ? update(currentSelections)
              : update,
        };
      });
    },
    [organizationId],
  );

  const isLibraryView =
    view === "library-categories" || view === "library-assets";
  const shouldReadCloudResources = Boolean(
    organizationId && isOpen && isLibraryView,
  );
  const allResourcesQuery = useGetMyFilesResourcesInfiniteQuery(
    { organizationId, resourceType: "all", search: "" },
    { skip: !shouldReadCloudResources },
  );
  const filteredResourcesQuery = useGetMyFilesResourcesInfiniteQuery(
    { organizationId, resourceType, search: "" },
    { skip: !shouldReadCloudResources || resourceType === "all" },
  );
  const [accessResource] = useGetMyFilesResourceAccessUrlMutation();
  const {
    data: allResourcesData,
    fetchNextPage: fetchNextAllResourcesPage,
    hasNextPage: hasNextAllResourcesPage,
    isFetchingNextPage: isFetchingNextAllResourcesPage,
  } = allResourcesQuery;

  const allResources = useMemo(
    () => flattenMyFilesResourcePages(allResourcesData?.pages),
    [allResourcesData],
  );
  const filteredResources = useMemo(
    () =>
      resourceType === "all"
        ? allResources
        : flattenMyFilesResourcePages(filteredResourcesQuery.data?.pages),
    [allResources, filteredResourcesQuery.data, resourceType],
  );
  const activeResourcesQuery =
    resourceType === "all" ? allResourcesQuery : filteredResourcesQuery;
  const previewUrls =
    previewState.organizationId === organizationId
      ? previewState.urls
      : EMPTY_PREVIEW_URLS;
  const attachments =
    controlledAttachments ??
    (attachmentState.organizationId === organizationId
      ? attachmentState.attachments
      : EMPTY_ATTACHMENTS);

  const requestPreviewUrl = useCallback(
    (resourceId: string) => {
      const cachedUrl =
        previewState.organizationId === organizationId
          ? previewState.urls[resourceId]
          : "";
      if (cachedUrl) return Promise.resolve(cachedUrl);
      if (!organizationId) {
        return Promise.reject(new Error("当前没有可用的组织"));
      }
      const requestKey = `${organizationId}:${resourceId}`;
      const currentRequest = previewRequestsRef.current.get(requestKey);
      if (currentRequest) return currentRequest;

      const request = accessResource({ organizationId, resourceId })
        .unwrap()
        .then((result) => {
          setPreviewState((current) => ({
            organizationId,
            urls: {
              ...(current.organizationId === organizationId
                ? current.urls
                : {}),
              [resourceId]: result.url,
            },
          }));
          return result.url;
        })
        .finally(() => {
          previewRequestsRef.current.delete(requestKey);
        });
      previewRequestsRef.current.set(requestKey, request);
      return request;
    },
    [accessResource, organizationId, previewState],
  );

  const requestDownloadUrl = useCallback(
    async (resourceId: string) => {
      if (!organizationId) throw new Error("当前没有可用的组织");
      const result = await accessResource({
        download: true,
        organizationId,
        resourceId,
      }).unwrap();
      return result.url;
    },
    [accessResource, organizationId],
  );

  useEffect(() => {
    if (
      !shouldReadCloudResources ||
      !hasNextAllResourcesPage ||
      isFetchingNextAllResourcesPage ||
      (allResourcesData?.pages.length ?? 0) >= MAX_CATEGORY_COUNT_PAGES
    ) {
      return;
    }
    void fetchNextAllResourcesPage();
  }, [
    allResourcesData?.pages.length,
    fetchNextAllResourcesPage,
    hasNextAllResourcesPage,
    isFetchingNextAllResourcesPage,
    shouldReadCloudResources,
  ]);

  useEffect(() => {
    if (!shouldReadCloudResources || view !== "library-assets") return;

    const previewCandidates = [
      ...filteredResources,
      ...allResources.filter((resource) => resource.resourceType !== "file"),
    ]
      .filter((resource) => resource.resourceType !== "file")
      .slice(0, 10);

    for (const resource of previewCandidates) {
      if (previewUrls[resource.resourceId]) continue;
      void requestPreviewUrl(resource.resourceId).catch(() => undefined);
    }
  }, [
    allResources,
    filteredResources,
    previewUrls,
    requestPreviewUrl,
    shouldReadCloudResources,
    view,
  ]);

  const catalog = useMemo(() => {
    const nextCatalog = !organizationId
      ? emptyMerryChatAddMenuCatalog
      : buildMerryChatAddMenuCatalog({
          allResources,
          hasMoreAllResources: Boolean(hasNextAllResourcesPage),
          previewUrls,
          visibleResources: filteredResources,
        });
    return contentMode === "resources-only"
      ? { ...nextCatalog, capabilities: [], tools: [] }
      : nextCatalog;
  }, [
    allResources,
    contentMode,
    filteredResources,
    hasNextAllResourcesPage,
    organizationId,
    previewUrls,
  ]);
  const extensionViewModels = useMemo(
    () => buildMerryChatExtensionViewModels(selectedExtensions, catalog),
    [catalog, selectedExtensions],
  );

  const assetState: MerryChatAddMenuAssetState = !organizationId
    ? "error"
    : activeResourcesQuery.isError
      ? "error"
      : activeResourcesQuery.isLoading ||
          (shouldReadCloudResources && activeResourcesQuery.isUninitialized)
        ? "loading"
        : "ready";

  useEffect(() => {
    onResourceRefsChange?.(readyChatResourceIds(attachments));
  }, [attachments, onResourceRefsChange]);

  const commitAttachments = useCallback(
    (nextAttachments: ReadonlyArray<ChatAttachment>) => {
      if (controlledAttachments === undefined) {
        setAttachmentState({
          attachments: nextAttachments,
          organizationId,
        });
      }
      onAttachmentsChange?.(nextAttachments);
    },
    [
      controlledAttachments,
      onAttachmentsChange,
      organizationId,
    ],
  );

  const updateOpen = (nextIsOpen: boolean) => {
    setIsOpen(nextIsOpen);
    if (nextIsOpen && contentMode === "resources-only") {
      setView("library-categories");
    } else if (!nextIsOpen) {
      setOverflowExtensionIds([]);
      setView("default");
    }
  };

  const handleCategorySelect = (category: MerryChatAddMenuCategory) => {
    setActiveCategoryId(category.id);
    setResourceType(categoryResourceType(category));
  };

  const toggleResource = (resourceId: string) => {
    const selected = attachments.some(
      (attachment) => attachment.resourceId === resourceId,
    );
    if (selected) {
      commitAttachments(
        attachments.filter(
          (attachment) => attachment.resourceId !== resourceId,
        ),
      );
      return;
    }
    const resource = filteredResources.find(
      (candidate) => candidate.resourceId === resourceId,
    );
    if (!resource) return;
    commitAttachments(
      mergeChatAttachments(attachments, [readyAttachmentFromResource(resource)]),
    );
  };

  const handleSend = async (message: string) => {
    if (hasUnreadyChatAttachments(attachments)) {
      throw new ChatInputError("请等待附件上传完成后再发送");
    }
    const attachmentSnapshot = attachments;
    const activeExtensionSnapshot = activeExtensionId;
    const extensionSnapshot = selectedExtensions;
    const serializedExtensions = serializeMerryChatExtensions(
      extensionSnapshot,
    );
    const resourceRefs = readyChatResourceRefs(attachmentSnapshot);
    const capabilityIntent = buildMerchantChatCapabilityIntent(
      extensionSnapshot,
      resourceRefs,
    );

    commitAttachments([]);
    setActiveExtensionId(undefined);
    setSelectedExtensions([]);
    try {
      await onSend?.(message, {
        ...(capabilityIntent ? { capabilityIntent } : {}),
        ...(serializedExtensions.length > 0
          ? { extensions: serializedExtensions }
          : {}),
        resourceIds: resourceRefs.map((resource) => resource.resource_id),
        resourceRefs,
      });
    } catch (error) {
      commitAttachments(attachmentSnapshot);
      setActiveExtensionId(activeExtensionSnapshot);
      setSelectedExtensions(extensionSnapshot);
      throw error;
    }
  };

  const selectTool = (tool: (typeof catalog.tools)[number]) => {
    setSelectedExtensions((current) =>
      upsertMerryChatExtension(current, createToolExtensionSelection(tool)),
    );
    updateOpen(false);
  };

  const selectCapability = (
    capability: (typeof catalog.capabilities)[number],
  ) => {
    const selection = createCapabilityExtensionSelection(
      capability,
      capability.kind === "video"
        ? videoExtensionParameters(videoParameters)
        : imageExtensionParameters(imageParameters),
    );
    setSelectedExtensions((current) =>
      upsertMerryChatExtension(
        current.filter((extension) => extension.kind !== "capability"),
        selection,
      ),
    );
    setActiveExtensionId(selection.id);
  };

  const configureExtension = (extensionId: string) => {
    const extension = selectedExtensions.find(
      (candidate) => candidate.id === extensionId,
    );
    if (!extension || extension.kind !== "capability") return;

    setActiveExtensionId(extension.id);
    const capability = catalog.capabilities.find(
      (candidate) => candidate.id === extension.definitionId,
    );
    if (!capability) return;

    if (capability.kind === "video") {
      const parameters =
        extension.parameters?.type === "video"
          ? {
              aspectRatio: extension.parameters.aspectRatio,
              duration: extension.parameters.duration,
              resolution: extension.parameters.resolution,
              sound: extension.parameters.sound,
            }
          : defaultMerryVideoParameters;
      setVideoParameters(parameters);
      setView("video-parameters");
    } else {
      const parameters =
        extension.parameters?.type === "image"
          ? {
              aspectRatio: extension.parameters.aspectRatio,
              longEdge: extension.parameters.longEdge,
            }
          : defaultMerryImageParameters;
      setImageParameters(parameters);
      setView("image-parameters");
    }
    setIsOpen(true);
  };

  const removeExtension = (extensionId: string) => {
    setSelectedExtensions((current) =>
      removeMerryChatExtension(current, extensionId),
    );
    if (activeExtensionId === extensionId) {
      setActiveExtensionId(undefined);
      updateOpen(false);
    }
  };

  const updateVideoExtensionParameters = (
    parameters: MerryVideoParameters,
  ) => {
    setVideoParameters(parameters);
    if (!activeExtensionId) return;
    setSelectedExtensions((current) =>
      updateMerryChatExtensionParameters(
        current,
        activeExtensionId,
        videoExtensionParameters(parameters),
      ),
    );
  };

  const updateImageExtensionParameters = (
    parameters: MerryImageParameters,
  ) => {
    setImageParameters(parameters);
    if (!activeExtensionId) return;
    setSelectedExtensions((current) =>
      updateMerryChatExtensionParameters(
        current,
        activeExtensionId,
        imageExtensionParameters(parameters),
      ),
    );
  };

  const removeAttachment = useCallback(
    (attachmentId: string) => {
      commitAttachments(
        attachments.filter(
          (attachment) => attachment.attachmentId !== attachmentId,
        ),
      );
    },
    [attachments, commitAttachments],
  );

  const handleUploadInput = (event: ChangeEvent<HTMLInputElement>) => {
    const files = [...(event.currentTarget.files ?? [])];
    event.currentTarget.value = "";
    if (isUploadDisabled) {
      setUploadNotice("云存储已用完，暂时无法上传新文件");
      return;
    }
    setUploadNotice(onUploadFiles?.(files) ?? "");
    updateOpen(false);
  };

  return (
    <div
      className={className}
      data-composer-presentation={presentation}
      data-chat-extension-count={selectedExtensions.length}
      data-chat-resource-count={attachments.length}
      data-testid="chat-add-menu-composer"
      ref={triggerRef}
    >
      <input
        aria-hidden="true"
        className="sr-only"
        multiple
        onChange={handleUploadInput}
        ref={fileInputRef}
        tabIndex={-1}
        type="file"
      />
      {attachments.length > 0 ? (
        <ChatAttachmentTray
          attachments={attachments}
          onRemove={removeAttachment}
          onRequestDownloadUrl={requestDownloadUrl}
          onRequestPreviewUrl={requestPreviewUrl}
          onRetry={(attachmentId) => {
            if (isUploadDisabled) {
              setUploadNotice("云存储已用完，暂时无法重试上传");
              return;
            }
            onRetryAttachment?.(attachmentId);
          }}
          previewUrls={previewUrls}
        />
      ) : null}
      <ChatAddInput
        presentation={presentation}
        {...inputProps}
        speechScopeKey={inputProps.speechScopeKey ?? organizationId}
        extensions={extensionViewModels}
        isAddMenuOpen={isOpen}
        isExtensionMoreOpen={isOpen && view === "extension-overflow"}
        isSendDisabled={
          isAISendDisabled || hasUnreadyChatAttachments(attachments)
        }
        onAdd={() => updateOpen(!isOpen)}
        onConfigureExtension={configureExtension}
        onExtensionMore={(hiddenExtensions) => {
          setOverflowExtensionIds(
            hiddenExtensions.map((extension) => extension.id),
          );
          setView("extension-overflow");
          setIsOpen(true);
        }}
        onRemoveExtension={removeExtension}
        onSend={onSend ? handleSend : undefined}
      />
      <Popover
        className="merry-chat-add-menu-popover"
        data-composer-presentation={presentation}
        crossOffset={0}
        isOpen={isOpen}
        offset={12}
        onOpenChange={updateOpen}
        placement={view === "extension-overflow" ? "top end" : presentation === "figma" && view === "default" ? "bottom start" : "top start"}
        shouldFlip
        triggerRef={triggerRef}
      >
        <Dialog
          aria-label={
            view === "extension-overflow" ? "更多已添加扩展" : "添加内容菜单"
          }
          className="outline-none"
        >
          <MerryChatAddMenu
            presentation={presentation}
            activeCategoryId={activeCategoryId}
            assetState={assetState}
            {...(!organizationId
              ? { assetStateMessage: "登录后可读取组织云盘" }
              : {})}
            catalog={catalog}
            data-testid="merry-chat-add-menu"
            imageParameters={imageParameters}
            navigationMode={contentMode}
            onAssetSelect={(asset) => {
              if (asset.kind === "resource") toggleResource(asset.id);
            }}
            onCategorySelect={handleCategorySelect}
            onCapabilitySelect={selectCapability}
            onImageParametersChange={updateImageExtensionParameters}
            onOverflowExtensionSelect={(extensionId) => {
              const extension = extensionViewModels.find(
                (candidate) => candidate.id === extensionId,
              );
              if (extension?.configurable) configureExtension(extensionId);
              else updateOpen(false);
            }}
            {...(organizationId
              ? {
                  onRetryAssets: () => {
                    void activeResourcesQuery.refetch();
                  },
                }
              : {})}
            onToolSelect={selectTool}
            {...(onUploadFiles || onAuthenticationRequired
              ? {
                  onUpload: () => {
                    if (!organizationId && onAuthenticationRequired) {
                      const text = triggerRef.current?.querySelector<HTMLTextAreaElement>("textarea")?.value ?? "";
                      onAuthenticationRequired(text, { extensions: serializeMerryChatExtensions(selectedExtensions), resourceIds: [], resourceRefs: [] });
                      updateOpen(false);
                      return;
                    }
                    if (isUploadDisabled) {
                      setUploadNotice("云存储已用完，暂时无法上传新文件");
                      updateOpen(false);
                      return;
                    }
                    fileInputRef.current?.click();
                  },
                }
              : {})}
            onVideoParametersChange={updateVideoExtensionParameters}
            onViewChange={(nextView) => {
              if (!organizationId && onAuthenticationRequired && (nextView === "library-categories" || nextView === "library-assets")) {
                const text = triggerRef.current?.querySelector<HTMLTextAreaElement>("textarea")?.value ?? "";
                onAuthenticationRequired(text, { extensions: serializeMerryChatExtensions(selectedExtensions), resourceIds: [], resourceRefs: [] });
                updateOpen(false);
                return;
              }
              if (
                contentMode === "resources-only" &&
                nextView !== "library-categories" &&
                nextView !== "library-assets"
              ) {
                return;
              }
              setView(nextView);
            }}
            overflowExtensions={extensionViewModels.filter((extension) =>
              overflowExtensionIds.includes(extension.id),
            )}
            selectedAssetIds={readyChatResourceIds(attachments)}
            videoParameters={videoParameters}
            view={view}
          />
        </Dialog>
      </Popover>
      {uploadNotice ? (
        <p className="merry-chat-add-menu-composer__upload-notice" role="status">
          {uploadNotice}
        </p>
      ) : null}
    </div>
  );
}
